/**
 * ルートプラン作成
 *
 * 「条件から提案」と「スポットを選ぶ」の 2 通り。
 * どちらも最適化はスポット数ぶん課金されるため、実行前に消費見込みを出す。
 */

import { Loader2, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'sonner'

import { createRoutePlan, getDefaultOrigin, previewSuggestion, suggestRoutePlan } from '@/api/services/route-plan'
import { getRallyMapItems, getStampRallies, getStampRally } from '@/api/services/stamp-rally'
import type { SuggestPlanParams, SuggestPreview } from '@/api/types/route-plan'
import type { PlaceSearchResult, StampRallyItem, StampRallySummary } from '@/api/types/stamp-rally'
import { Header } from '@/components/shared/header'
import { RallyMapPicker, type RallyPlaceGroup } from '@/components/shared/map/rally-map-picker'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useGoBack } from '@/hooks/useGoBack'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'

import { PlanConditionFields } from '../components/plan-condition-fields'
import { SelectAllRow } from '../components/select-all-row'
import { SpotSearchField } from '../components/spot-search-field'
import type { PlanCondition } from '../lib'
import { addToSelection, buildDepartAt, isConditionReady, SPLIT_DAYS_STATE } from '../lib'

type CreateMode = 'suggest' | 'manual'

type ManualSource = 'rally' | 'map' | 'other'

/** 手動選択のリストに並べる 1 件。ラリーをまたぐときだけラリー名が付く */
type ManualItem = StampRallyItem & { rally_title?: string }

const DEFAULT_RADIUS_KM = 30
const DEFAULT_MAX_CANDIDATES = 30

const pad = (value: number): string => String(value).padStart(2, '0')

/** 地図から navigate(state) で渡されたスポット ID */
const presetItemIdsOf = (state: unknown): number[] => {
  if (typeof state !== 'object' || state === null || !('itemIds' in state)) return []
  const ids = (state as { itemIds: unknown }).itemIds
  if (!Array.isArray(ids)) return []
  return ids.filter((value): value is number => typeof value === 'number')
}

const todayLocal = (): string => {
  const now = new Date()
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** 地図の場所をリストの 1 行として持つ。複数ラリーに入っていれば未獲得の分を優先する */
const pickedItemOf = (place: RallyPlaceGroup): ManualItem =>
  place.items.find((item) => !item.stamped_at) ?? place.items[0]

const initialCondition = (): PlanCondition => ({
  title: '',
  planDate: todayLocal(),
  departTime: '09:00',
  travelMode: 'driving',
  returnToOrigin: true,
  defaultStayMinutes: 30,
  maxDurationMinutes: null,
  originLat: null,
  originLon: null,
  originName: '',
  originPlaceId: null,
  destLat: null,
  destLon: null,
  destName: '',
  destPlaceId: null,
})

const toSuggestParams = (
  condition: PlanCondition,
  radiusKm: number,
  rallyIds: number[],
  maxCandidates: number,
): SuggestPlanParams | null => {
  const departAt = buildDepartAt(condition.planDate, condition.departTime)
  if (departAt === null || condition.originLat === null || condition.originLon === null) return null
  return {
    title: condition.title.trim() || undefined,
    origin_lat: condition.originLat,
    origin_lon: condition.originLon,
    origin_name: condition.originName.trim() || null,
    depart_at: departAt,
    travel_mode: condition.travelMode,
    max_duration_minutes: condition.maxDurationMinutes,
    return_to_origin: condition.returnToOrigin,
    dest_lat: condition.destLat,
    dest_lon: condition.destLon,
    dest_name: condition.destName.trim() || null,
    radius_km: radiusKm,
    rally_ids: rallyIds.length > 0 ? rallyIds : undefined,
    max_candidates: maxCandidates,
    plan_date: condition.planDate || null,
  }
}

export const RoutePlanCreatePage = () => {
  const navigate = useNavigate()
  const goBack = useGoBack()
  const location = useLocation()
  const presetItemIds = useMemo(() => presetItemIdsOf(location.state), [location.state])

  const [mode, setMode] = useState<CreateMode>(presetItemIds.length > 0 ? 'manual' : 'suggest')
  const [condition, setCondition] = useState<PlanCondition>(initialCondition)
  const [rallies, setRallies] = useState<StampRallySummary[]>([])
  const [selectedRallyIds, setSelectedRallyIds] = useState<number[]>([])
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM)
  const [preview, setPreview] = useState<SuggestPreview | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [allowMultiDay, setAllowMultiDay] = useState(false)
  const { isSubmitting, run } = useSubmitLock()

  const [manualRallyId, setManualRallyId] = useState<number | null>(null)
  const [manualSource, setManualSource] = useState<ManualSource>('rally')
  const [manualItems, setManualItems] = useState<ManualItem[]>([])
  // ラリーを切り替えても消えないよう、地図で選んだ場所と検索して足した場所は別に持つ。
  // 検索した場所はまだ Place が無いので ID ではなく検索結果のまま送る
  const [mapPicked, setMapPicked] = useState<ManualItem[]>([])
  const [extraSpots, setExtraSpots] = useState<PlaceSearchResult[]>([])
  const [isItemsLoading, setIsItemsLoading] = useState(false)
  const [selectedPlaceIds, setSelectedPlaceIds] = useState<number[]>([])
  // 地図は開いたときに初めて作る（再マウントすると取得と表示範囲がやり直しになる）
  const [hasOpenedMap, setHasOpenedMap] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    void (async () => {
      try {
        setRallies(await getStampRallies(undefined, controller.signal))
        const origin = await getDefaultOrigin(controller.signal)
        if (!origin) return
        setCondition((prev) =>
          prev.originLat !== null
            ? prev
            : { ...prev, originLat: origin.latitude, originLon: origin.longitude, originName: origin.name ?? '' },
        )
      } catch {
        if (!controller.signal.aborted) toast.error('初期値の取得に失敗しました')
      }
    })()
    return () => controller.abort()
  }, [])

  const loadManualItems = useCallback(async (rallyId: number) => {
    setIsItemsLoading(true)
    try {
      const detail = await getStampRally(rallyId)
      setManualItems(detail.groups.flatMap((group) => group.items).filter((item) => !item.stamped_at))
    } catch {
      toast.error('スポットの取得に失敗しました')
    } finally {
      setIsItemsLoading(false)
    }
  }, [])

  // 地図で選んだ分はラリーをまたぐので、ID を直接引いて名前を埋める
  useEffect(() => {
    if (presetItemIds.length === 0) return
    const controller = new AbortController()
    setIsItemsLoading(true)
    getRallyMapItems({ item_ids: presetItemIds }, controller.signal)
      .then((items) => {
        setMapPicked(items)
        setSelectedPlaceIds(items.map((item) => item.place_id))
      })
      .catch(() => {
        if (!controller.signal.aborted) toast.error('スポットの取得に失敗しました')
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsItemsLoading(false)
      })
    return () => controller.abort()
  }, [presetItemIds])

  const handleSelectManualRally = (rallyId: number) => {
    setManualRallyId(rallyId)
    // 前のラリーの行はリストから消えるので、見えない選択が残らないよう地図で選んだ分だけ残す
    setSelectedPlaceIds((prev) => prev.filter((id) => mapPicked.some((item) => item.place_id === id)))
    void loadManualItems(rallyId)
  }

  const handleSpotPicked = (result: PlaceSearchResult) => {
    setExtraSpots((prev) => (prev.some((e) => e.google_place_id === result.google_place_id) ? prev : [...prev, result]))
    toast.success(`${result.name} を追加しました`)
  }

  const handleRemoveSpot = (googlePlaceId: string | null) =>
    setExtraSpots((prev) => prev.filter((spot) => spot.google_place_id !== googlePlaceId))

  const handlePreview = async () => {
    const params = toSuggestParams(condition, radiusKm, selectedRallyIds, DEFAULT_MAX_CANDIDATES)
    if (!params) return
    setIsPreviewing(true)
    try {
      setPreview(await previewSuggestion(params))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '候補の取得に失敗しました')
    } finally {
      setIsPreviewing(false)
    }
  }

  const openPlan = (planId: number) =>
    navigate(PATHS.ROUTE_PLANS.DETAIL.replace(':plan_id', String(planId)), {
      replace: true,
      state: allowMultiDay ? SPLIT_DAYS_STATE : undefined,
    })

  const handleSuggest = async () => {
    const params = toSuggestParams(condition, radiusKm, selectedRallyIds, DEFAULT_MAX_CANDIDATES)
    if (!params) return
    await run(async () => {
      try {
        // 日分けで改めて最適化するので、1 日分は回さず枠を温存する
        const plan = await suggestRoutePlan({ ...params, optimize: !allowMultiDay })
        openPlan(plan.id)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'プランの作成に失敗しました')
      }
    })
  }

  const handleCreateManual = async () => {
    await run(async () => {
      try {
        const plan = await createRoutePlan({
          title: condition.title.trim() || undefined,
          place_ids: selectedPlaceIds,
          spots: extraSpots.filter((spot): spot is PlaceSearchResult & { google_place_id: string } =>
            Boolean(spot.google_place_id),
          ),
          plan_date: condition.planDate || null,
          travel_mode: condition.travelMode,
          depart_at: buildDepartAt(condition.planDate, condition.departTime),
          origin_lat: condition.originLat,
          origin_lon: condition.originLon,
          origin_name: condition.originName.trim() || null,
          return_to_origin: condition.returnToOrigin,
          dest_lat: condition.destLat,
          dest_lon: condition.destLon,
          dest_name: condition.destName.trim() || null,
          default_stay_minutes: condition.defaultStayMinutes,
          max_duration_minutes: condition.maxDurationMinutes,
        })
        openPlan(plan.id)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'プランの作成に失敗しました')
      }
    })
  }

  const toggleRally = (rallyId: number) =>
    setSelectedRallyIds((prev) => (prev.includes(rallyId) ? prev.filter((id) => id !== rallyId) : [...prev, rallyId]))

  const toggleItem = (placeId: number) =>
    setSelectedPlaceIds((prev) => (prev.includes(placeId) ? prev.filter((id) => id !== placeId) : [...prev, placeId]))

  // 地図で選んだ場所はラリーをまたぐので、下のリストへ足して選択済みとして扱う
  const toggleMapPlace = (place: RallyPlaceGroup) => {
    const picked = pickedItemOf(place)
    setMapPicked((prev) => (prev.some((item) => item.place_id === place.placeId) ? prev : [...prev, picked]))
    toggleItem(place.placeId)
  }

  const selectMapPlaces = (places: RallyPlaceGroup[]) => {
    const next = addToSelection(
      selectedPlaceIds,
      places.map((place) => place.placeId),
    )
    const kept = places.filter((place) => next.includes(place.placeId))
    setMapPicked((prev) => {
      const known = new Set(prev.map((item) => item.place_id))
      return [...prev, ...kept.filter((place) => !known.has(place.placeId)).map(pickedItemOf)]
    })
    setSelectedPlaceIds(next)
  }

  const ready = isConditionReady(condition)
  const totalSelected = selectedPlaceIds.length + extraSpots.length

  const manualList = useMemo<ManualItem[]>(() => {
    const pickedPlaceIds = new Set(mapPicked.map((item) => item.place_id))
    return [...mapPicked, ...manualItems.filter((item) => !pickedPlaceIds.has(item.place_id))]
  }, [mapPicked, manualItems])
  const unselectedManualIds = manualList.map((item) => item.place_id).filter((id) => !selectedPlaceIds.includes(id))

  return (
    <div className="flex h-dvh flex-col">
      <Header title="ルートプランを作成" onClose={goBack} />

      <Tabs
        value={mode}
        onValueChange={(value) => {
          setMode(value as CreateMode)
          setPreview(null)
        }}
        className="px-3 pt-3"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="suggest">条件から提案</TabsTrigger>
          <TabsTrigger value="manual">スポットを選ぶ</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-3 pb-8">
        <PlanConditionFields value={condition} onChange={setCondition} />

        <div className="space-y-1">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={allowMultiDay} onCheckedChange={(checked) => setAllowMultiDay(checked === true)} />
            複数日（日帰り × N 日）に分けてもよい
          </label>
          {allowMultiDay && (
            <p className="text-xs text-muted-foreground">
              作成後に候補日を選ぶ画面が開きます。上の日付・出発時刻は毎日の出発条件として使われます
            </p>
          )}
        </div>

        <div className={cn('space-y-4', mode !== 'suggest' && 'hidden')}>
          <div className="space-y-1.5">
            <Label htmlFor="plan-radius">出発地からの距離（km）</Label>
            <Input
              id="plan-radius"
              type="number"
              min={1}
              max={300}
              value={radiusKm}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
            />
          </div>

          <div className="space-y-2">
            <Label>対象のラリー（未選択なら全部）</Label>
            {rallies.map((rally) => (
              <label key={rally.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                <Checkbox checked={selectedRallyIds.includes(rally.id)} onCheckedChange={() => toggleRally(rally.id)} />
                <span className="min-w-0 flex-1 truncate">{rally.title}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  残り {rally.total_count - rally.stamped_count}
                </span>
              </label>
            ))}
          </div>

          {preview && (
            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              <p>
                候補 <span className="tabular-nums">{preview.candidate_count}</span> 件。 最適化すると{' '}
                <span className="tabular-nums font-medium">{preview.estimated_shipments}</span> 回ぶんの枠を消費します
              </p>
              <p className="mt-1 text-xs text-muted-foreground">採用件数ではなく、投入したスポット数で課金されます</p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              disabled={!ready || isPreviewing}
              onClick={() => void handlePreview()}
            >
              {isPreviewing ? <Loader2 size={16} className="animate-spin" /> : '候補を確認'}
            </Button>
            <Button
              className="flex-1 gap-2"
              disabled={!ready || isSubmitting || preview?.candidate_count === 0}
              onClick={() => void handleSuggest()}
            >
              {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              提案する
            </Button>
          </div>
        </div>

        <div className={cn('space-y-4', mode !== 'manual' && 'hidden')}>
          <Tabs
            value={manualSource}
            onValueChange={(value) => {
              const next = value as ManualSource
              if (next === 'map') setHasOpenedMap(true)
              setManualSource(next)
            }}
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="rally">ラリーから</TabsTrigger>
              <TabsTrigger value="map">地図から</TabsTrigger>
              <TabsTrigger value="other">その他の場所</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className={cn('space-y-2', manualSource !== 'map' && 'hidden')}>
            {hasOpenedMap && (
              <div className="h-[60vh] overflow-hidden rounded-lg border">
                <RallyMapPicker
                  rallies={rallies}
                  selectedPlaceIds={selectedPlaceIds}
                  onToggle={toggleMapPlace}
                  onClear={() => setSelectedPlaceIds([])}
                  onSelectAll={selectMapPlaces}
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              ピンをタップして選ぶと、下のリストに並びます。上の絞り込みでラリーや条件を選び「すべて選択」でまとめて選べます
            </p>
          </div>

          <div className={cn('space-y-2', manualSource !== 'other' && 'hidden')}>
            <Label>場所を検索して追加</Label>
            <SpotSearchField
              origin={
                condition.originLat === null || condition.originLon === null
                  ? null
                  : { latitude: condition.originLat, longitude: condition.originLon }
              }
              onPicked={handleSpotPicked}
            />
            <p className="text-xs text-muted-foreground">スタンプの対象にはならない、立ち寄り先として追加されます</p>

            {extraSpots.length > 0 && (
              <div className="space-y-2">
                {extraSpots.map((spot) => (
                  <div
                    key={spot.google_place_id ?? `${spot.latitude},${spot.longitude}`}
                    className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate">{spot.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 px-2!"
                      onClick={() => handleRemoveSpot(spot.google_place_id)}
                    >
                      外す
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={cn('space-y-2', manualSource !== 'rally' && 'hidden')}>
            <Label>ラリーを選ぶ</Label>
            <div className="flex flex-wrap gap-2">
              {rallies.map((rally) => (
                <Button
                  key={rally.id}
                  variant={manualRallyId === rally.id ? 'default' : 'outline'}
                  size="sm"
                  className="px-3!"
                  onClick={() => handleSelectManualRally(rally.id)}
                >
                  {rally.title}
                </Button>
              ))}
            </div>
          </div>

          {isItemsLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-lg" />
              ))}
            </div>
          ) : manualList.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {manualRallyId === null ? '地図かラリーから選ぶとスポットが並びます' : '未獲得のスポットがありません'}
            </p>
          ) : (
            <div className="space-y-2">
              <SelectAllRow
                count={manualList.length}
                unselectedCount={unselectedManualIds.length}
                onSelectAll={() => setSelectedPlaceIds(addToSelection(selectedPlaceIds, unselectedManualIds))}
              />
              {manualList.map((item) => (
                <label key={item.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                  <Checkbox
                    checked={selectedPlaceIds.includes(item.place_id)}
                    onCheckedChange={() => toggleItem(item.place_id)}
                  />
                  <span className="min-w-0 flex-1 truncate">{item.name}</span>
                  {item.rally_title && (
                    <span className="shrink-0 text-xs text-muted-foreground">{item.rally_title}</span>
                  )}
                </label>
              ))}
            </div>
          )}

          <Button
            className="w-full"
            disabled={!ready || totalSelected === 0 || isSubmitting}
            onClick={() => void handleCreateManual()}
          >
            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : `${totalSelected} 件でプランを作る`}
          </Button>
        </div>
      </div>
    </div>
  )
}
